import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import CommentIcon from '@mui/icons-material/Comment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';

import { ErrorAlert, SuccessAlert } from '../components/Alerts';
import Loading from '../components/Loading';
import apiService from '../services/api';
import liffService from '../services/liff';
import { StoreUser, StoreDetail } from '../types';
import { useAuth } from '../auth/AuthContext';
import AddressDisplay from '../components/AddressDisplay';

const UserStoreManagement: React.FC = () => {
  const navigate = useNavigate();
  const { storeUserId } = useParams<{ storeUserId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [store, setStore] = useState<StoreUser | null>(null);
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [storeDetail, setStoreDetail] = useState<StoreDetail | null>(null);

  // Edit Memo Dialog State
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editedMemo, setEditedMemo] = useState<string>('');

  // Delete Store Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const { userId } = useAuth();

  useEffect(() => {
    const fetchStore = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getStoreUser(userId);
        if (response.success && response.data) {
          setStoreUsers(response.data);
          const found = response.data.find(
            (s: StoreUser) => String(s.id) === storeUserId
          );
          if (found) {
            setStore(found);
            // 店舗詳細（住所・電話番号）を取得
            const detailRes = await apiService.getStore(found.store_id);
            if (detailRes.success && detailRes.data) {
              setStoreDetail(detailRes.data);
            }
          }
        }
      } catch (err) {
        setError('店舗データの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [userId, storeUserId]);

  // Open edit memo dialog
  const handleOpenEditDialog = () => {
    setEditedMemo(store?.memo || '');
    setOpenEditDialog(true);
  };

  // Close edit memo dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  // Update memo
  const handleUpdateMemo = async () => {
    if (!store) return;

    try {
      setLoading(true);
      await apiService.updateStoreMemo(store.id, editedMemo);
      setSuccess('メモを更新しました');
      setStore(prev => prev ? { ...prev, memo: editedMemo } : prev);
      handleCloseEditDialog();
    } catch (err) {
      setError('メモの更新に失敗しました。後でもう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // Delete store
  const handleDeleteStore = async () => {
    if (!store) return;

    try {
      setLoading(true);
      const response = await apiService.deleteBookmark(store.id);

      if (!response.error) {
        // 解除した店舗が「直近に経由した店舗」としてキャッシュされていると、
        // 次回起動時に店舗登録ダイアログが再表示されるため、他の登録店舗へ差し替える
        const fallback = storeUsers.find((s) => s.store_id !== store.store_id);
        liffService.handleStoreUnregistered(store.store_id, fallback?.store_id);
        setSuccess(`${store.store_name}を削除しました`);
        setDeleteDialogOpen(false);
        navigate('/mypage');
      } else {
        setError('削除に失敗しました。もう一度お試しください。');
      }
    } catch (err) {
      setError('削除に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pt: 0, px: 0, pb: 2, m: 0 }}>
      {loading && <Loading />}
      {error && <ErrorAlert message={error} onClose={() => setError(null)} />}
      {success && <SuccessAlert message={success} onClose={() => setSuccess(null)} />}

      {/* ページヘッダー */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        pt: 1.5,
        pb: 0.5,
        minHeight: 48,
      }}>
        <IconButton onClick={() => navigate('/mypage')} size="small" sx={{ color: '#3c3c3c' }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography sx={{
          flex: 1,
          fontWeight: 700,
          fontSize: '16px',
          color: '#3c3c3c',
          pl: 0.5,
        }}>
          店舗詳細
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : !store ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', my: 4 }}>
            店舗情報が見つかりません
          </Typography>
        ) : (
          <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ pb: 1 }}>
              {/* 店舗名 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {store.store_name}
                </Typography>
              </Box>

              {/* 住所 */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <LocationOnIcon fontSize="small" sx={{ mr: 1, mt: 0.2, color: 'text.secondary', flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">住所</Typography>
                  <AddressDisplay address={storeDetail || undefined} fallbackAddress={storeDetail?.address} />
                </Box>
              </Box>

              {/* 電話番号 */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, mt: 0.2, color: 'text.secondary', flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">電話番号</Typography>
                  <Typography variant="body2">
                    {storeDetail?.phone || '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* メモ */}
              {store.memo ? (
                <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CommentIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">メモ</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {store.memo}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  メモはありません
                </Typography>
              )}
            </CardContent>

            <Divider />

            <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={handleOpenEditDialog}
                sx={{ mr: 1 }}
              >
                メモを編集
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                解除
              </Button>
            </CardActions>
          </Card>
        )}
      </Box>

      {/* Edit Memo Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>メモを編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="memo-description"
            label="メモ"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={editedMemo}
            onChange={(e) => setEditedMemo(e.target.value)}
            variant="outlined"
            placeholder="この店舗へのメモを入力してください"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleUpdateMemo}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          店舗解除の確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {store && `「${store.store_name}」を登録店舗から解除してもよろしいですか？`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleDeleteStore} color="error" autoFocus>
            解除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserStoreManagement;
